import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
import { ArticleCategoryRef } from './article.js';
import { ArticleStatusSchema, PlantillaBloqueSchema } from './enums.js';
import { MaquetaPortadaSchema } from './maqueta-portada.js';
import { ORDEN_CATEGORIA_TOPE, PORTADA_POSICIONES } from './orden-notas.js';
import { esCantidadValida } from './plantillas.js';
import { UrlKeySchema } from './url-key.js';
/**
 * Cómo se ve la página principal del medio: qué bloques la componen, en qué orden, y qué notas van
 * en cada uno.
 *
 * Un **bloque** es una franja de la página principal. Hay de cuatro tipos y todos son filas de
 * `collections`:
 *
 *  - **Portada**: la curan a mano, tiene posiciones fijas y siempre va primera. No se apaga ni se
 *    mueve.
 *  - **Última hora**: se calcula (las últimas publicadas). No se cura, no se mueve —va siempre
 *    atrás de la portada— y no se apaga: en escritorio es la columna lateral de todo el sitio. Lo
 *    único suyo que se edita es el rótulo.
 *  - **Más leídas**: se calcula con las lecturas registradas por el sitio público (ver
 *    `lecturas.ts`). Se mueve, se apaga y se renombra, y se configura como uno de categoría: el
 *    editor elige disposición y cantidad.
 *  - **Categoría**: una por categoría del medio. Se calcula por fecha, pero la redacción puede
 *    intervenir el orden (ver `orden-notas.ts`).
 */
export const BloqueKindSchema = z.enum(['PORTADA', 'ULTIMA_HORA', 'MAS_LEIDAS', 'CATEGORIA']);
/** Un rótulo de franja: entra en una línea del sitio y del panel. */
export const BLOQUE_NAME_MAX = 40;
/**
 * Los bloques que **no se mueven** van primeros y en este orden. Los dos son estructura del sitio,
 * no una decisión editorial: la portada abre la página y Última hora es la tira que la acompaña
 * (en escritorio vive en la columna lateral, que no tiene otro lugar donde estar).
 *
 * La regla es una sola y la comparten el panel —que no dibuja el asa— y el servidor, que rechaza
 * el pedido igual: que la interfaz no ofrezca algo no impide mandarlo a mano.
 */
export const BLOQUES_FIJOS = ['PORTADA', 'ULTIMA_HORA'];
/** `true` si ese bloque se puede arrastrar a otro lugar de la página. */
export function bloqueSeMueve(kind) {
    return !BLOQUES_FIJOS.includes(kind);
}
/**
 * `true` si ese bloque se puede sacar de la página principal.
 *
 * Los fijos, no: son la estructura del sitio. La portada abre la página y Última hora es la tira
 * que la acompaña —en escritorio, la columna lateral entera—, así que apagarla dejaría media
 * pantalla vacía en las cuatro páginas del sitio, no solo en la home.
 */
export function bloqueSeApaga(kind) {
    return !BLOQUES_FIJOS.includes(kind);
}
/**
 * Los bloques cuyo rótulo es **suyo** y no de otra cosa. El de un bloque de categoría **es** el de
 * la categoría (se cambia ahí, y así el bloque y su página propia no quedan con nombres distintos),
 * y el de la portada no se dibuja en ningún lado —abre la página sin rótulo—, así que cambiarlo no
 * se vería.
 */
export const BLOQUES_CON_NOMBRE_PROPIO = ['ULTIMA_HORA', 'MAS_LEIDAS'];
/** `true` si el nombre del bloque se edita desde el orden de la página. */
export function bloqueSeRenombra(kind) {
    return BLOQUES_CON_NOMBRE_PROPIO.includes(kind);
}
/**
 * `true` si el editor elige la disposición y la cantidad de notas de ese bloque
 * (ver `PresentacionBloque` y `plantillas.ts`).
 *
 * Los dos bloques que son una **lista calculada de notas sueltas** —una categoría y Más leídas—
 * se dibujan con el mismo repertorio de plantillas, así que se configuran igual. La portada tiene
 * su propia grilla curada de 6 posiciones, y Última hora es la lista larga del día que en
 * escritorio vive en la columna lateral: ninguna de las dos tiene disposición que elegir.
 */
export function bloqueLlevaPresentacion(kind) {
    return kind === 'CATEGORIA' || kind === 'MAS_LEIDAS';
}
/**
 * `true` si ese bloque puede estar en la **fila de la cabecera** del sitio.
 *
 * Todos menos la portada: su página es `/`, a donde ya va el logo, así que un "Portada" en el menú
 * repetiría el clic más obvio de la pantalla. Los otros tres tienen una página propia a la que
 * llevar —la de la categoría, `/ultima-hora` y `/mas-leidas`—, y es lo que hace que el link no sea
 * una promesa vacía.
 *
 * Ojo con lo que **no** decide: estar en la cabecera es distinto de estar en el sitio
 * (`bloqueSeApaga`). Un bloque puede estar apagado en la portada y seguir en el menú, y al revés.
 */
export function bloqueVaAlHeader(kind) {
    return kind !== 'PORTADA';
}
/** Curada = la elige la redacción. Automática = la resuelve el sistema. */
export const BloqueOrigenSchema = z.enum(['CURADA', 'AUTOMATICA']);
/** Lo mínimo de una nota para dibujarla en una lista de orden. */
export const NotaReferencia = z
    .object({
    id: UuidSchema,
    title: z.string(),
    urlKey: UrlKeySchema,
    /** Miniatura de portada; `null` si no tiene o si el bucket no es público. */
    coverUrl: z.string().nullable(),
    publishedAt: z.string().datetime().nullable(),
    /**
     * Sirve para avisar: una nota que se archivó sigue ocupando su lugar guardado hasta que alguien
     * lo suelte, y el panel tiene que poder decir "esta ya no está en vivo".
     */
    status: ArticleStatusSchema,
    categories: z.array(ArticleCategoryRef),
    author: z.string(),
})
    .strict();
/**
 * La disposición de un bloque y cuántas notas trae. **Viajan juntas**: la cantidad solo tiene
 * sentido contra una plantilla (4 es una tira de una página, y en lista no existe), así que
 * validarlas por separado obligaría a mirar lo que hay guardado. Acá el par se valida entero.
 *
 * Las cantidades válidas y el porqué están en `plantillas.ts`.
 */
export const PresentacionBloque = z
    .object({
    plantilla: PlantillaBloqueSchema,
    notas: z.number().int().positive(),
})
    .strict()
    .refine(({ plantilla, notas }) => esCantidadValida(plantilla, notas), {
    message: 'Esa cantidad de notas no existe en esa disposición',
    path: ['notas'],
});
export const BloqueDTO = z
    .object({
    id: UuidSchema,
    kind: BloqueKindSchema,
    name: z.string(),
    /** Una línea que explica qué se ve en ese bloque, para la columna "Qué muestra". */
    queMuestra: z.string(),
    origen: BloqueOrigenSchema,
    /** `false` = el bloque no se dibuja en la página principal. Los fijos no se pueden apagar. */
    isActive: z.boolean(),
    /**
     * `true` = la sección aparece en la **fila de la cabecera** del sitio.
     *
     * Es independiente de `isActive`: una sección puede estar en el menú y no tener franja en la
     * portada, o al revés. Lo que decide es la navegación, no el contenido.
     */
    enElHeader: z.boolean(),
    position: z.number().int().min(0),
    /** Solo en los bloques de categoría. */
    categoryId: UuidSchema.nullable(),
    /**
     * Lo que eligió el editor para este bloque (los que la llevan: ver `bloqueLlevaPresentacion`).
     * `null` = lo elige la rotación automática del sitio, que es como quedan todos los bloques
     * hasta que alguien los toca.
     */
    presentacion: PresentacionBloque.nullable(),
})
    .strict();
export const OrdenarBloquesRequest = z
    .object({
    bloques: z
        .array(z.object({ id: UuidSchema, position: z.number().int().min(0) }).strict())
        .min(1)
        .max(200),
})
    .strict();
/**
 * El detalle de un bloque. Los campos son **opcionales y se aplican solo si vienen**: los switches de
 * "En el sitio" y "En el header" y el selector de disposición son controles distintos de la misma
 * fila, y mandar uno no tiene que pisar los otros.
 *
 * `presentacion: null` es explícito y significa "volver a la rotación automática"; la clave ausente
 * significa "no la toques".
 */
export const ActualizarBloqueRequest = z
    .object({
    /** Solo los bloques que llevan nombre propio (ver `bloqueSeRenombra`). */
    name: z.string().trim().min(1).max(BLOQUE_NAME_MAX).optional(),
    isActive: z.boolean().optional(),
    /** Solo los que pueden estar en la cabecera (ver `bloqueVaAlHeader`). */
    enElHeader: z.boolean().optional(),
    presentacion: PresentacionBloque.nullable().optional(),
})
    .strict()
    .refine((input) => input.name !== undefined ||
    input.isActive !== undefined ||
    input.enElHeader !== undefined ||
    input.presentacion !== undefined, { message: 'No hay nada que cambiar' });
/* ───────────────────────────── portada ───────────────────────────── */
export const PortadaSlot = z
    .object({
    /** 1 es la principal. */
    position: z.number().int().min(1).max(PORTADA_POSICIONES),
    /** `null` = hueco. La portada es curada: los huecos no se autocompletan. */
    nota: NotaReferencia.nullable(),
})
    .strict();
export const PortadaResponse = z
    .object({
    /**
     * La maqueta con la que se dibuja la portada en el sitio. Es lo que decide `posiciones`.
     */
    maqueta: MaquetaPortadaSchema,
    /** Cuántas posiciones **dibuja** la maqueta elegida (ver `posicionesDeMaqueta`). */
    posiciones: z.number().int().min(1),
    /**
     * Las `PORTADA_POSICIONES` guardadas, siempre todas. Las que caen más allá de `posiciones` son
     * las que la maqueta actual no muestra: siguen curadas y vuelven a verse al cambiar de maqueta.
     */
    slots: z.array(PortadaSlot),
})
    .strict();
export const GuardarPortadaRequest = z
    .object({
    /**
     * La maqueta va **en el mismo guardado** que las notas: elegir la maqueta y acomodar la portada
     * son la misma decisión editorial, y se revisa entera antes de aplicarla.
     */
    maqueta: MaquetaPortadaSchema,
    /**
     * Se manda la portada **entera**, incluidas las posiciones que la maqueta elegida no dibuja:
     * guardarlas es lo que permite volver a A sin rearmar nada.
     */
    slots: z
        .array(z
        .object({
        position: z.number().int().min(1).max(PORTADA_POSICIONES),
        articleId: UuidSchema,
    })
        .strict())
        .max(PORTADA_POSICIONES),
})
    .strict();
/* ──────────────────── orden de las notas de una categoría ──────────────────── */
export const NotaOrdenada = z
    .object({
    position: z.number().int().min(1),
    /** Clavada en este número (pin). Si no, su lugar es relativo y las notas nuevas la empujan. */
    fijada: z.boolean(),
    nota: NotaReferencia,
})
    .strict();
export const OrdenCategoriaResponse = z
    .object({
    categoryId: UuidSchema,
    tope: z.number().int().min(1),
    items: z.array(NotaOrdenada),
    /**
     * Notas que quedaron fijadas pero ya no están publicadas: no ocupan lugar, y se listan aparte
     * para que se pueda soltar el pin en vez de dejar una fila fantasma.
     */
    fijadasSinPublicar: z.array(NotaReferencia),
})
    .strict();
/**
 * Se guarda **la ventana completa**, no solo las fijadas: es lo único que permite que una nota
 * movida a mano conserve su lugar relativo cuando se publica una nueva.
 */
export const GuardarOrdenCategoriaRequest = z
    .object({
    items: z
        .array(z
        .object({
        position: z.number().int().min(1).max(ORDEN_CATEGORIA_TOPE),
        articleId: UuidSchema,
        fijada: z.boolean(),
    })
        .strict())
        .max(ORDEN_CATEGORIA_TOPE),
})
    .strict();
/* ───────────────────────────── errores ───────────────────────────── */
export const SITE_LAYOUT_ERROR_CODES = {
    bloqueNoEncontrado: 'BLOQUE_NO_ENCONTRADO',
    /** La portada siempre va primera. */
    portadaNoSeMueve: 'PORTADA_NO_SE_MUEVE',
    /** Última hora va siempre atrás de la portada. */
    ultimaHoraNoSeMueve: 'ULTIMA_HORA_NO_SE_MUEVE',
    /** La portada no se puede sacar de la página. */
    portadaNoSeApaga: 'PORTADA_NO_SE_APAGA',
    /** Última hora tampoco: es la columna lateral de todo el sitio. */
    ultimaHoraNoSeApaga: 'ULTIMA_HORA_NO_SE_APAGA',
    /** Se intentó renombrar un bloque cuyo nombre no es suyo (ver `bloqueSeRenombra`). */
    bloqueNoSeRenombra: 'BLOQUE_NO_SE_RENOMBRA',
    /**
     * Se le eligió disposición a un bloque que no la lleva (ver `bloqueLlevaPresentacion`): la
     * portada tiene su propia grilla curada y Última hora es la lista larga del día.
     */
    presentacionNoDisponible: 'PRESENTACION_NO_DISPONIBLE',
    /** Se intentó poner en la cabecera un bloque que no va (ver `bloqueVaAlHeader`): la portada. */
    bloqueNoVaAlHeader: 'BLOQUE_NO_VA_AL_HEADER',
    posicionesRepetidas: 'ORDEN_POSICIONES_REPETIDAS',
    /** Se intentó poner una nota que no está publicada, o que no es de esa categoría o medio. */
    notaNoDisponible: 'ORDEN_NOTA_NO_DISPONIBLE',
};
/** Claves de los bloques que provisiona la plataforma. */
export const BLOQUE_KEY = {
    portada: 'portada',
    ultimaHora: 'ultima-hora',
    masLeidas: 'mas-leidas',
};
/** El bloque de una categoría se identifica por su categoría, no por un nombre que puede cambiar. */
export function bloqueKeyDeCategoria(categoryId) {
    return `categoria:${categoryId}`;
}
